import produce from 'immer';

import { FormExternalConditionData, FormStateValues } from '@components/form';
import { shouldDisplayField } from '@components/form/form-utils';
import { LocaleCode } from '@locale/locale-store';
import { randomId } from '@utils/strings';

import { postal_code_to_city } from './postal-codes';
import { Field, FieldText, RepeatingFieldEntry, RepeatingFieldTemplate } from './types';

export const prepareFormData = (
  form: Field[],
  values: FormStateValues,
  externalData: FormExternalConditionData
) => {
  const merged = mergeFormValues(form, values, externalData);
  const withDerived = setDerivedFieldValues(merged);
  return formToFormData(withDerived);
};

export const bootstrapForm = <T extends Field>(form: T[]) => {
  return bootstrapRepeatingFields(form);
};

export const bootstrapRepeatingFields = <T extends Field>(form: T[]) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (field.type === 'repeating') {
        field.entries = [createRepatingEntry(field.template)];
      }

      if ('fields' in field) {
        field.fields = bootstrapRepeatingFields(field.fields);
      }
    });
  });
};

export const removeRepeatingEntry = <T extends Field>(id: string, form: T[]) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (field.type === 'repeating') {
        field.entries = (field.entries ?? []).filter((entry) => entry.id !== id);
      }

      if ('fields' in field) {
        field.fields = removeRepeatingEntry(id, field.fields);
      }
    });
  });
};

export const addRepeatingEntry = <T extends Field>(id: string, form: Array<T>) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (field.id === id && field.type === 'repeating') {
        field.entries = (field.entries ?? []).concat(createRepatingEntry(field.template));
      }

      if ('fields' in field) {
        field.fields = addRepeatingEntry(id, field.fields);
      }
    });
  });
};

export const createRepatingEntry = <T extends RepeatingFieldTemplate>(
  template: T
): RepeatingFieldEntry<T> => {
  return {
    id: randomId(),
    fields: produce(template, (draft) => {
      draft.forEach((field) => {
        field.id = `${field.id}::${randomId()}`;

        if (field.type === 'formGroup') {
          field.fields.forEach((field) => {
            field.id = `${field.id}::${randomId()}`;
          });
        }
      });
    }),
  };
};

export const getLanguageKeysInForm = <T extends Field>(form: Array<T>) => {
  const languages = new Set<string>();

  form.forEach((field) => {
    if ('fields' in field) {
      const childKeys = getLanguageKeysInForm(field.fields);
      childKeys.forEach((key) => languages.add(key));
    }

    if ('text' in field) {
      Object.keys(field.text).forEach((key) => languages.add(key));
    }
  });

  return Array.from(languages.values()) as Array<LocaleCode>;
};

export const setDerivedFieldValues = <T extends Field>(form: T[]) => {
  return produce(form, (draft) => {
    return draft.forEach((field) => {
      if (field.type === 'derived') {
        const val = findValueInFormByName(form, field.derivedFrom);

        if (val && field.derivedThrough === 'postal_code_to_city') {
          field.value = postal_code_to_city(val);
        } else {
          field.value = val;
        }
      }

      if ('fields' in field) {
        field.fields = setDerivedFieldValues(field.fields);
      }
    });
  });
};

const findValueInFormByName = <T extends Field>(form: T[], name: string) => {
  let val;

  form.forEach((item) => {
    if ('name' in item && item.name === name && 'value' in item && item.value) {
      // Ignore TS because value doesn't exist on all fields
      // but if we have item.value then it should be a value field.
      // @ts-ignore
      val = item.value;
    }

    if (item.type === 'conditional' || item.type === 'formGroup') {
      const childVal = findValueInFormByName(item.fields, name);
      if (childVal) {
        val = childVal;
      }
      return;
    }

    if (item.type === 'repeating' && item.entries) {
      item.entries.forEach((entry) => {
        const childVal = findValueInFormByName(entry.fields, name);
        if (childVal) {
          val = childVal;
        }
      });
    }
  });

  return val;
};

export const getTranslatedFormText = (text: FieldText | undefined, lang: LocaleCode): string => {
  if (!text) return '';
  if (typeof text !== 'object') return text ?? '';

  const translation = text[lang] || text['en'] || text['is'];
  if (translation) return translation;

  const keys = Object.keys(text) as LocaleCode[];
  return text[keys[0]] ?? '';
};

export const mergeFormValues = <T extends Field>(
  form: T[],
  state: FormStateValues,
  data: FormExternalConditionData
) => {
  return produce(form, (draft) => {
    return draft.forEach((field) => {
      if (field.type === 'conditional') {
        field.shown = shouldDisplayField(field.condition, data, state);
      }

      if ('fields' in field) {
        field.fields = mergeFormValues(field.fields, state, data);
      }

      if (field.type === 'repeating') {
        field.entries = (field.entries ?? []).map((entry) => ({
          ...entry,
          fields: mergeFormValues(entry.fields, state, data),
        }));
      }

      if (field.id in state) {
        // value doesn't exist on all fields so it will error
        // but we know if the state contains this field, it has to be a value field
        // @ts-ignore
        field.value = state[field.id];
      }
    });
  });
};

export const formToFormData = <T extends Field>(form: T[]): FormData => {
  let files: Record<string, File[]> = {};

  const extractedForm = extractFilesFromForm(form, files);

  const formData = new FormData();
  formData.append('form', JSON.stringify(extractedForm));
  formData.append('isComplete', 'true');

  Object.keys(files).forEach((id) => {
    files[id].forEach((file, index) => {
      formData.append(`${id}__${index}`, file);
    });
  });

  return formData;
};

const extractFilesFromForm = <T extends Field>(form: T[], files: Record<string, File[]>) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if ((field.type === 'file' || field.type === 'file-multi') && field.value) {
        const key = field.name ? `${field.name}__${field.id}` : field.id;
        files[key] = (files[key] ?? []).concat(field.value);
        field.value = undefined;
      }

      if ('fields' in field) {
        field.fields = extractFilesFromForm(field.fields, files);
      }

      if ('entries' in field && field.entries) {
        field.entries = field.entries.map((entry) => ({
          ...entry,
          fields: extractFilesFromForm(entry.fields, files),
        }));
      }
    });
  });
};

export const addFieldIdToForm = <T extends Field>(form: T[]) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (!field.id) {
        field.id = randomId();
      }

      if ('fields' in field) {
        field.fields = addFieldIdToForm(field.fields);
      }

      if ('template' in field) {
        field.template = addFieldIdToForm(field.template);
      }
    });
  });
};
